import { useGetAdmissionResults } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Select from '@components/V4/Select/Select';
import {
  AcademicCapIcon,
  BadgeCheckIcon,
  GlobeIcon,
  LibraryIcon,
  TrendingUpIcon,
} from '@heroicons/react/outline';
import {
  ColumnHeader,
  EmptyState,
  FilterField,
  MetricCard,
  SectionHeading,
  TableShell,
} from '@modules/Admin/Reporting/common';
import { Checkbox } from 'antd';
import moment from 'moment';
import { ReactNode, useMemo, useState } from 'react';
import {
  ALL_TIME,
  applyContextFilters,
  applyOutcomeFilter,
  buildResultsCsv,
  computeSummary,
  Filters,
  formatResultDate,
  groupByCountry,
  groupByMentor,
  groupByPeriod,
  groupByUniversity,
  LAST_12_MONTHS,
  OUTCOME_OPTIONS,
  OutcomeFilter,
  statusLabel,
} from './admissionResults.helpers';

const PAGE_SIZE = 100;
const TOP_SIZE = 25;

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow =
  'border-b border-white/5 align-top transition-colors last:border-b-0 hover:bg-surfaceHover';
const leftHead = 'px-5 py-3 text-left font-semibold';
const leftCell = 'px-5 py-3 text-left text-customLight';
const numberCell = 'px-5 py-3 text-right tabular-nums';

const Chip = ({ children }: { children: ReactNode }) => (
  <span className="rounded-full bg-surfaceHover px-2.5 py-1 text-xs text-customLight">
    {children}
  </span>
);

const ShowAllToggle = (props: { total: number; expanded: boolean; onToggle: () => void }) =>
  props.total > TOP_SIZE ? (
    <button
      type="button"
      className="self-start text-sm text-customPrimary"
      onClick={props.onToggle}
    >
      {props.expanded ? `Show top ${TOP_SIZE}` : `Show all ${props.total}`}
    </button>
  ) : null;

const AdmissionResults = () => {
  const { data, isLoading } = useGetAdmissionResults();
  const rows = useMemo(() => data?.rows ?? [], [data]);

  const [filters, setFilters] = useState<Filters>({
    period: ALL_TIME,
    outcome: 'admitted',
    countries: [],
    universities: [],
    mentors: [],
    verifiedOnly: false,
  });
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [expanded, setExpanded] = useState({ universities: false, mentors: false });

  const updateFilters = (patch: Partial<Filters>) => {
    setFilters((previous) => ({ ...previous, ...patch }));
    setVisibleCount(PAGE_SIZE);
  };

  const options = useMemo(() => {
    const years = [
      ...new Set(rows.filter((row) => row.resultDate).map((row) => moment(row.resultDate).year())),
    ].sort((a, b) => b - a);

    const byId = <T,>(items: [string, T][]) => [...new Map(items).entries()];

    return {
      periods: [
        { value: ALL_TIME, label: 'All time' },
        { value: LAST_12_MONTHS, label: 'Last 12 months' },
        ...years.map((year) => ({ value: year.toString(), label: year.toString() })),
      ],
      countries: [...new Set(rows.map((row) => row.country).filter(Boolean))]
        .sort()
        .map((country) => ({ value: country, label: country })),
      universities: byId(rows.map((row) => [row.universityId, row.universityName]))
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([value, label]) => ({ value, label })),
      mentors: byId(
        rows.flatMap((row) =>
          row.mentors.map((mentor) => [mentor.id, mentor.name] as [string, string])
        )
      )
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([value, label]) => ({ value, label })),
    };
  }, [rows]);

  const contextRows = useMemo(() => applyContextFilters(rows, filters), [rows, filters]);
  const filteredRows = useMemo(
    () => applyOutcomeFilter(contextRows, filters.outcome),
    [contextRows, filters.outcome]
  );

  const summary = useMemo(() => computeSummary(contextRows), [contextRows]);
  const byUniversity = useMemo(() => groupByUniversity(filteredRows), [filteredRows]);
  const byMentor = useMemo(() => groupByMentor(filteredRows), [filteredRows]);
  const byCountry = useMemo(() => groupByCountry(filteredRows), [filteredRows]);
  const isYearSelected = /^\d{4}$/.test(filters.period);
  const byPeriod = useMemo(
    () => groupByPeriod(filteredRows, isYearSelected || filters.period === LAST_12_MONTHS),
    [filteredRows, isYearSelected, filters.period]
  );

  const resultsLabel = filters.outcome === 'rejected' ? 'Rejections' : 'Results';

  const exportCsv = () => {
    const blob = new Blob([`\uFEFF${buildResultsCsv(filteredRows)}`], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `admission-results-${moment().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 p-8 font-raleway text-white">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold">Admission Results</h1>
          <p className="max-w-3xl text-sm text-customGrey">
            University results recorded on student profiles. Mentors are the ones who held sessions
            with the student before the result date.
          </p>
        </div>
        <Button
          variant="secondary"
          buttonText="Export CSV"
          disabled={!filteredRows.length}
          onClick={exportCsv}
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <FilterField width="tablet:w-44">
          <Select
            variant="secondary"
            label="Period"
            value={filters.period}
            allowClear={false}
            options={options.periods}
            onChange={(value) => updateFilters({ period: String(value ?? ALL_TIME) })}
          />
        </FilterField>
        <FilterField width="tablet:w-52">
          <Select
            variant="secondary"
            label="Result"
            value={filters.outcome}
            allowClear={false}
            options={OUTCOME_OPTIONS}
            onChange={(value) => updateFilters({ outcome: value as OutcomeFilter })}
          />
        </FilterField>
        <FilterField width="tablet:w-56">
          <Select
            variant="secondary"
            label="Country"
            mode="multiple"
            showSearch
            placeholder="Any country"
            value={filters.countries}
            options={options.countries}
            onChange={(value) => updateFilters({ countries: (value as string[]) ?? [] })}
          />
        </FilterField>
        <FilterField width="tablet:w-64">
          <Select
            variant="secondary"
            label="University"
            mode="multiple"
            showSearch
            optionFilterProp="label"
            placeholder="Any university"
            value={filters.universities}
            options={options.universities}
            onChange={(value) => updateFilters({ universities: (value as string[]) ?? [] })}
          />
        </FilterField>
        <FilterField width="tablet:w-64">
          <Select
            variant="secondary"
            label="Mentor"
            mode="multiple"
            showSearch
            optionFilterProp="label"
            placeholder="Any mentor"
            value={filters.mentors}
            options={options.mentors}
            onChange={(value) => updateFilters({ mentors: (value as string[]) ?? [] })}
          />
        </FilterField>
        <Checkbox
          className="pb-2 text-white"
          checked={filters.verifiedOnly}
          onChange={(event) => updateFilters({ verifiedOnly: event.target.checked })}
        >
          Only with admission document
        </Checkbox>
      </div>

      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 laptop:grid-cols-5">
        <MetricCard
          label="Students admitted"
          value={summary.admittedStudents}
          icon={<AcademicCapIcon className="h-5 w-5" />}
          accent="teal"
        />
        <MetricCard
          label="Offers received"
          value={summary.offers}
          icon={<BadgeCheckIcon className="h-5 w-5" />}
          accent="gold"
        />
        <MetricCard
          label="Offers accepted"
          value={summary.offersAccepted}
          icon={<TrendingUpIcon className="h-5 w-5" />}
          accent="green"
        />
        <MetricCard
          label="Universities"
          value={summary.universities}
          icon={<LibraryIcon className="h-5 w-5" />}
          accent="lila"
        />
        <MetricCard
          label="Acceptance rate"
          value={summary.acceptanceRate === null ? 'n/a' : `${summary.acceptanceRate}%`}
          icon={<GlobeIcon className="h-5 w-5" />}
          accent="teal"
        />
      </div>

      {!filteredRows.length ? (
        <EmptyState>No admission results match these filters.</EmptyState>
      ) : (
        <>
          <section className="flex flex-col gap-4">
            <SectionHeading
              title={isYearSelected ? 'By month' : 'By year'}
              description="Grouped by the result date. Results without a date are listed separately."
            />
            <TableShell>
              <thead>
                <tr className={headRow}>
                  <th className={leftHead}>Period</th>
                  <ColumnHeader dot="teal">Students</ColumnHeader>
                  <ColumnHeader dot="gold">{resultsLabel}</ColumnHeader>
                </tr>
              </thead>
              <tbody>
                {byPeriod.map((period) => (
                  <tr key={period.key} className={bodyRow}>
                    <td className={leftCell}>{period.label}</td>
                    <td className={numberCell}>{period.students}</td>
                    <td className={numberCell}>{period.results}</td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
          </section>

          <section className="flex flex-col gap-4">
            <SectionHeading title="By university" />
            <TableShell>
              <thead>
                <tr className={headRow}>
                  <th className={leftHead}>University</th>
                  <th className={leftHead}>Country</th>
                  <ColumnHeader dot="teal">Students</ColumnHeader>
                  <ColumnHeader dot="gold">{resultsLabel}</ColumnHeader>
                </tr>
              </thead>
              <tbody>
                {(expanded.universities ? byUniversity : byUniversity.slice(0, TOP_SIZE)).map(
                  (university) => (
                    <tr key={university.id} className={bodyRow}>
                      <td className={leftCell}>{university.name}</td>
                      <td className={leftCell}>{university.country || '—'}</td>
                      <td className={numberCell}>{university.students}</td>
                      <td className={numberCell}>{university.results}</td>
                    </tr>
                  )
                )}
              </tbody>
            </TableShell>
            <ShowAllToggle
              total={byUniversity.length}
              expanded={expanded.universities}
              onToggle={() =>
                setExpanded((previous) => ({ ...previous, universities: !previous.universities }))
              }
            />
          </section>

          <section className="flex flex-col gap-4">
            <SectionHeading
              title="By mentor"
              description="A student counts for every mentor who held at least one session with them before the result, so the totals add up to more than the number of students."
            />
            <TableShell minWidth="min-w-[820px]">
              <thead>
                <tr className={headRow}>
                  <th className={leftHead}>Mentor</th>
                  <ColumnHeader dot="teal">Students</ColumnHeader>
                  <ColumnHeader dot="gold">{resultsLabel}</ColumnHeader>
                  <th className={leftHead}>Courses</th>
                </tr>
              </thead>
              <tbody>
                {(expanded.mentors ? byMentor : byMentor.slice(0, TOP_SIZE)).map((mentor) => (
                  <tr key={mentor.id} className={bodyRow}>
                    <td className={leftCell}>{mentor.name}</td>
                    <td className={numberCell}>{mentor.students}</td>
                    <td className={numberCell}>{mentor.results}</td>
                    <td className="px-5 py-3 text-left">
                      <div className="flex flex-wrap gap-2">
                        {mentor.courses.map((course) => (
                          <Chip key={course}>{course}</Chip>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
            <ShowAllToggle
              total={byMentor.length}
              expanded={expanded.mentors}
              onToggle={() =>
                setExpanded((previous) => ({ ...previous, mentors: !previous.mentors }))
              }
            />
          </section>

          <section className="flex flex-col gap-4">
            <SectionHeading title="By country" />
            <TableShell>
              <thead>
                <tr className={headRow}>
                  <th className={leftHead}>Country</th>
                  <ColumnHeader dot="teal">Students</ColumnHeader>
                  <ColumnHeader dot="gold">{resultsLabel}</ColumnHeader>
                </tr>
              </thead>
              <tbody>
                {byCountry.map((country) => (
                  <tr key={country.country} className={bodyRow}>
                    <td className={leftCell}>{country.country}</td>
                    <td className={numberCell}>{country.students}</td>
                    <td className={numberCell}>{country.results}</td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
          </section>

          <section className="flex flex-col gap-4">
            <SectionHeading
              title={`All results (${filteredRows.length})`}
              description="A date starting with ~ is estimated from when the result was last edited, because no results date was filled in."
            />
            <TableShell minWidth="min-w-[1100px]">
              <thead>
                <tr className={headRow}>
                  <th className={leftHead}>Student</th>
                  <th className={leftHead}>University</th>
                  <th className={leftHead}>Major</th>
                  <th className={leftHead}>Result</th>
                  <th className={leftHead}>Date</th>
                  <th className={leftHead}>Mentors</th>
                  <th className={leftHead}>Document</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.slice(0, visibleCount).map((row) => (
                  <tr
                    key={`${row.studentId}-${row.universityId}-${row.status}`}
                    className={bodyRow}
                  >
                    <td className={leftCell}>
                      {row.studentName}
                      {row.graduationYear ? (
                        <span className="block text-xs text-customGrey">
                          Class of {row.graduationYear}
                        </span>
                      ) : null}
                    </td>
                    <td className={leftCell}>
                      {row.universityName}
                      <span className="block text-xs text-customGrey">{row.country}</span>
                    </td>
                    <td className={leftCell}>{row.majors.join(', ') || '—'}</td>
                    <td className={leftCell}>{statusLabel(row.status)}</td>
                    <td className={`${leftCell} whitespace-nowrap`}>{formatResultDate(row)}</td>
                    <td className="px-5 py-3 text-left">
                      {row.mentors.length ? (
                        <div className="flex flex-wrap gap-2">
                          {row.mentors.map((mentor) => (
                            <Chip key={mentor.id}>
                              {mentor.name} · {mentor.sessions}
                            </Chip>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-customGrey">No sessions before result</span>
                      )}
                    </td>
                    <td className={leftCell}>{row.hasAdmissionDocument ? 'Yes' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
            {filteredRows.length > visibleCount ? (
              <button
                type="button"
                className="self-start text-sm text-customPrimary"
                onClick={() => setVisibleCount((previous) => previous + PAGE_SIZE)}
              >
                Show {Math.min(PAGE_SIZE, filteredRows.length - visibleCount)} more
              </button>
            ) : null}
          </section>
        </>
      )}
    </div>
  );
};

export default AdmissionResults;
